import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'latest-address');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = articles.filter((a) => a.categorySlug === activeSlug);

  useEffect(() => {
    document.title = `${activeCategory.name} – JavBus 使用指南`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', activeCategory.description);
    }
  }, [activeCategory]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="pt-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: activeCategory.name },
                ]}
              />
            </div>
            <div className="py-8 md:py-12">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className={`${activeCategory.icon} text-lg text-primary-600`}></i>
                </div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {activeCategory.name}
                </h1>
              </div>
              <p className="text-foreground-600 text-sm md:text-base">
                {activeCategory.description}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50 border-b border-background-200">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-3">
            <div className="flex items-center gap-2 overflow-x-auto flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article, index) => (
                  <article
                    key={article.slug}
                    className="bg-white rounded-lg overflow-hidden hover:bg-background-100 transition-colors cursor-pointer group animate-fade-in-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <Link to={`/article/${article.slug}`} className="block p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                          {article.category}
                        </span>
                        <span className="text-foreground-400 text-xs">{article.readTime}</span>
                      </div>
                      <h2 className="font-heading font-semibold text-foreground-900 text-sm mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-400 text-xs">{article.date}</span>
                        <div className="flex flex-wrap gap-1">
                          {article.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-foreground-400 text-xs">#{tag}</span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}